import moment from 'moment'
import { IScheduleSectionFormValues } from '@src/features/shift-post-v2/steps/shift-details-step/components/schedule-section/schedule-section.types'
import { INewDuplicateModalFormValues } from '@src/features/shift-post-v2/modals/new-duplicate-modal/new-duplicate-modal/new-duplicate-modal.form'
import { INewEditModalFormValues } from '@src/features/shift-post-v2/modals/new-edit-modal/new-edit-modal/new-edit-modal.form'
import { TDay } from '@src/ui/components/Admin/Shifts/ShiftEditDuplicate/utils/types'
import { getFinalDay } from '@src/ui/components/Admin/Shifts/ShiftEditDuplicate/utils'

// Map string weekdays to moment.js day numbers (0-6, where 0 is Sunday)
export const WEEKDAY_MAP: { [key: string]: number } = {
  Sun: 0,
  Mon: 1,
  Tue: 2,
  Wed: 3,
  Thu: 4,
  Fri: 5,
  Sat: 6,
}

/**
 * Generates day objects for recurring shifts based on selected weekdays and date range
 * Generates ALL days in the range with proper working/non-working status
 *
 * ID Assignment Logic (in order of priority):
 * 1. Dirty days (user-modified) preserve their original IDs and properties
 * 2. API data from date_ids preserves original IDs
 * 3. Existing days (marked as existing: true) preserve their IDs but get updated properties
 * 4. New days get new IDs starting from max existing ID + 1 (checks both values.days and date_ids)
 *
 * @param startDay Start day of the range
 * @param endDay End day of the range
 * @param selectedWeekdays Array of weekday strings (e.g., ['Mon', 'Wed', 'Fri'])
 * @param values Form values
 */
export const generateRecurringDays = (
  startDay: Date,
  endDay: Date,
  selectedWeekdays: string[],
  values: INewDuplicateModalFormValues | INewEditModalFormValues | IScheduleSectionFormValues
) => {
  const days: TDay[] = []
  const startMoment = moment(startDay)
  const endMoment = moment(endDay)
  const currentMoment = startMoment.clone()

  // Convert string weekdays to numbers
  const selectedWeekdayNumbers = selectedWeekdays
    .map((day) => WEEKDAY_MAP[day])
    .filter((num) => num !== undefined)

  // Create a map of existing days by date_start to preserve their IDs and properties
  const existingDaysMap = new Map<string, TDay>()
  values.days.forEach((day: TDay) => {
    if (day.is_dirty) {
      existingDaysMap.set(day.date_start, day)
    }
  })

  // Create a map of existing days by ID to preserve IDs for non-dirty days
  const existingDaysByIdMap = new Map<number, TDay>()
  values.days.forEach((day: TDay) => {
    existingDaysByIdMap.set(day.id, day)
  })

  // Get date_ids for ID calculation
  const date_ids = values.date_ids || {}

  // Find the highest existing ID to start from for new days only
  // Check both values.days and date_ids to get the true maximum
  const existingIds = values.days.map((day: TDay) => day.id)
  const dateIdsIds = Object.values(date_ids).map((day: TDay) => day.id)
  const allExistingIds = [...existingIds, ...dateIdsIds]
  const maxExistingId = allExistingIds.length > 0 ? Math.max(...allExistingIds) : -1
  let nextId = maxExistingId + 1

  let i = 0

  // Generate ALL days in the date range
  while (currentMoment.isSameOrBefore(endMoment)) {
    const dayOfWeek = currentMoment.day() // 0-6, where 0 is Sunday
    const isSelectedWeekday = selectedWeekdayNumbers.includes(dayOfWeek)

    const currentDayStart = currentMoment.clone()
    const currentDayEnd = getFinalDay(currentDayStart, values.hour_start, values.hour_end)

    const date_start = currentDayStart.format('MM/DD/YYYY')
    const date_end = currentDayEnd.format('MM/DD/YYYY')

    let day: TDay = {
      id: nextId++,
      day_start: currentDayStart,
      day_end: currentDayEnd,
      date_start,
      date_end,
      hour_start: values.hour_start,
      hour_end: values.hour_end,
      break: values.break,
      pay_value: values.pay_value,
      working: isSelectedWeekday, // Mark as working only if it's a selected weekday
      punch_cards_count: 0,
      existing: false,
      starting_day: false,
      is_dirty: false,
    }

    // Check if we have an existing day (marked as dirty) to preserve its ID and properties
    const existingDay = existingDaysMap.get(date_start)
    if (existingDay) {
      // Preserve the existing day's ID and properties
      day = {
        ...existingDay,
        working: existingDay.working ?? isSelectedWeekday, // Update working status based on current weekday selection
        existing: true,
        is_dirty: !!existingDay.is_dirty,
        // Keep the original is_dirty value (don't override it)
      }
    } else if (date_ids[date_start]) {
      day = {
        ...date_ids[date_start],
        id: date_ids[date_start].id,
        working: date_ids[date_start].working ?? isSelectedWeekday, // Update working status based on current weekday selection
        punch_cards_count: date_ids[date_start].punch_cards_count,
        existing: true,
        starting_day: date_ids[date_start].starting_day,
        is_dirty: !!date_ids[date_start].is_dirty,
        // Keep the original is_dirty value (don't override it)
      }
      // Fall back to date_ids if no existing day (this is for existing backend data)
    } else {
      // Check if this day matches an existing day by date_start to preserve its ID
      const existingDayByDate = values.days.find((d) => d.date_start === date_start)
      if (existingDayByDate) {
        day = {
          ...existingDayByDate,
          id: existingDayByDate.id,
          working: existingDayByDate.working ?? isSelectedWeekday, // Update working status based on current weekday selection
          existing: true,
          punch_cards_count: existingDayByDate.punch_cards_count,
          is_dirty: !!existingDayByDate.is_dirty, // Keep as non-dirty since it's not user-modified
        }
      } else if (date_ids[date_start]) {
        // Check date_ids for days that might have been filtered out
        const originalDay = date_ids[date_start] as TDay

        day = {
          ...originalDay,
          id: originalDay.id,
          working: originalDay.working ?? isSelectedWeekday, // Update working status based on current weekday selection
          existing: true,
          punch_cards_count: originalDay.punch_cards_count || 0,
          is_dirty: !!originalDay.is_dirty,
          starting_day: originalDay.starting_day,
        }
      }
      // If no existing day found, this is a new day and will get a new ID
    }

    days.push(day)
    currentMoment.add(1, 'day')
    i++
  }

  return days
}

/**
 * Incrementally updates day objects based on changes to weekdays selection or date range.
 *
 * Behavior:
 * - Weekday toggle (e.g., add/remove Monday): only updates existing days falling on those weekdays.
 *   Other days remain untouched (fields preserved). Dirty days are not overridden.
 * - Date range extension: only appends new days for the newly included dates. Existing days remain unchanged.
 * - Date range shrink: does not remove existing days; they are left as-is.
 *
 * ID Assignment for newly added days follows the same rules as generateRecurringDays:
 * - Preserve id from date_ids if available; otherwise use next incremental id after the max in values.days and date_ids.
 */
export const generateRecurringDaysIncremental = (
  prevStartDay: Date,
  prevEndDay: Date,
  prevSelectedWeekdays: string[],
  nextStartDay: Date,
  nextEndDay: Date,
  nextSelectedWeekdays: string[],
  values: INewDuplicateModalFormValues | INewEditModalFormValues | IScheduleSectionFormValues
) => {
  let updatedDays: TDay[] = [...values.days]

  const prevStart = moment(prevStartDay)
  const prevEnd = moment(prevEndDay)
  const nextStart = moment(nextStartDay)
  const nextEnd = moment(nextEndDay)

  const prevWeekdayNumbers = prevSelectedWeekdays
    .map((day) => WEEKDAY_MAP[day])
    .filter((num) => num !== undefined)

  const nextWeekdayNumbers = nextSelectedWeekdays
    .map((day) => WEEKDAY_MAP[day])
    .filter((num) => num !== undefined)

  const prevWeekdaySet = new Set<number>(prevWeekdayNumbers)
  const nextWeekdaySet = new Set<number>(nextWeekdayNumbers)

  const addedWeekdays: number[] = [...nextWeekdaySet].filter((d) => !prevWeekdaySet.has(d))
  const removedWeekdays: number[] = [...prevWeekdaySet].filter((d) => !nextWeekdaySet.has(d))

  // Map for quick lookup of existing days by date_start
  const existingByDate = new Map<string, TDay>()
  updatedDays.forEach((d) => existingByDate.set(d.date_start, d))

  const date_ids = values.date_ids || {}

  // Determine next id for new days
  const existingIds = updatedDays.map((d) => d.id)
  const dateIdsIds = Object.values(date_ids).map((d: TDay) => d.id)
  const maxExistingId = [...existingIds, ...dateIdsIds].length
    ? Math.max(...existingIds, ...dateIdsIds)
    : -1
  let nextId = maxExistingId + 1

  // Helper to create a new TDay for a specific calendar day
  const createDayForMoment = (m: moment.Moment): TDay => {
    const dayOfWeek = m.day()
    const start = m.clone()
    const end = getFinalDay(start, values.hour_start, values.hour_end)
    const date_start = start.format('MM/DD/YYYY')
    const date_end = end.format('MM/DD/YYYY')

    const day: TDay = {
      id: nextId++,
      day_start: start,
      day_end: end,
      date_start,
      date_end,
      hour_start: values.hour_start,
      hour_end: values.hour_end,
      break: values.break,
      pay_value: values.pay_value,
      working: nextWeekdaySet.has(dayOfWeek),
      punch_cards_count: 0,
      existing: false,
      starting_day: false,
      is_dirty: false,
    }

    if (date_ids[date_start]) {
      const originalDay = date_ids[date_start] as TDay
      day.id = originalDay.id
      day.working = originalDay.working ?? nextWeekdaySet.has(dayOfWeek)
      day.punch_cards_count = originalDay.punch_cards_count || 0
      day.existing = true
      day.starting_day = originalDay.starting_day
      day.is_dirty = true
    }

    return day
  }

  // 1) Handle weekday toggles: only update the matching weekdays among existing days; skip dirty days
  if (addedWeekdays.length || removedWeekdays.length) {
    for (let idx = 0; idx < updatedDays.length; idx++) {
      const day = updatedDays[idx]
      if (day.is_dirty) continue
      const dayOfWeek = moment(day.day_start).day()
      if (addedWeekdays.includes(dayOfWeek)) {
        updatedDays[idx] = { ...day, working: true }
      } else if (removedWeekdays.includes(dayOfWeek)) {
        updatedDays[idx] = { ...day, working: false }
      }
    }
  }

  // 2) Handle date range extension: only add new days outside the previous range, within the next range
  // Extend backwards
  if (nextStart.isBefore(prevStart)) {
    const cursor = nextStart.clone()
    while (cursor.isBefore(prevStart)) {
      const key = cursor.format('MM/DD/YYYY')
      if (!existingByDate.has(key)) {
        const newDay = createDayForMoment(cursor)
        updatedDays.push(newDay)
        existingByDate.set(key, newDay)
      }
      cursor.add(1, 'day')
    }
  }

  // Extend forwards
  if (nextEnd.isAfter(prevEnd)) {
    const cursor = prevEnd.clone().add(1, 'day')
    while (cursor.isSameOrBefore(nextEnd)) {
      const key = cursor.format('MM/DD/YYYY')
      if (!existingByDate.has(key)) {
        const newDay = createDayForMoment(cursor)
        updatedDays.push(newDay)
        existingByDate.set(key, newDay)
      }
      cursor.add(1, 'day')
    }
  }

  // 3) Handle date range shrink: remove days that fall outside the new [nextStart, nextEnd]
  updatedDays = updatedDays.filter((d) => {
    const m = moment(d.date_start, 'MM/DD/YYYY')
    return m.isSameOrAfter(nextStart, 'day') && m.isSameOrBefore(nextEnd, 'day')
  })

  // Keep stable order by date_start
  updatedDays.sort((a, b) =>
    moment(a.date_start, 'MM/DD/YYYY').diff(moment(b.date_start, 'MM/DD/YYYY'))
  )

  return updatedDays
}
